import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

const OTPDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [otp, setOtp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    fetchOTP();
  }, [id]);

  const fetchOTP = async () => {
    try {
      const res = await api.get(`/otp/${id}`);
      setOtp(res.data.data);
    } catch (error) {
      toast.error('Failed to load OTP details');
      navigate('/otp');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResending(true);
    try {
      await api.post(`/otp/${id}/resend`);
      toast.success('OTP resent successfully');
      fetchOTP(); // Refresh data
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'used': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!otp) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">OTP not found</p>
        <Link to="/otp" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          Back to OTP list
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">OTP Details</h1>
        <Link
          to="/otp"
          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
        >
          Back to List
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* OTP Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">OTP Code</label>
              <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                <span className="font-mono text-lg font-semibold text-gray-900">
                  {otp.code}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Purpose</label>
              <p className="mt-1 text-sm text-gray-900 capitalize">{otp.purpose}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(otp.status)}`}>
                {otp.status}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Created At</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(otp.createdAt)}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Expires At</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(otp.expiresAt)}</p>
            </div>
          </div>

          {/* Lead Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Lead Information</h3>

            {otp.leadDetails ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Patient Name</label>
                  <p className="mt-1 text-sm text-gray-900">{otp.leadDetails.patientName}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="mt-1 text-sm text-gray-900">{otp.leadDetails.phone}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Disease</label>
                  <p className="mt-1 text-sm text-gray-900">{otp.leadDetails.disease}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Lead Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                    otp.leadDetails.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    otp.leadDetails.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {otp.leadDetails.status}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Lead information not available</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex gap-4">
          {otp.status === 'active' && (
            <button
              onClick={handleResendOTP}
              disabled={resending}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {resending ? 'Resending...' : 'Resend OTP'}
            </button>
          )}

          <Link
            to={`/leads/${otp.leadId}`}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            View Lead
          </Link>
        </div>
      </div>

      {/* Usage History */}
      {otp.usageHistory && otp.usageHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Usage History</h3>
          <div className="space-y-3">
            {otp.usageHistory.map((usage, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {usage.action === 'generated' ? 'Generated' :
                     usage.action === 'resent' ? 'Resent' :
                     usage.action === 'verified' ? 'Verified' : 'Used'}
                  </p>
                  <p className="text-xs text-gray-600">{formatDate(usage.timestamp)}</p>
                </div>
                <span className="text-sm text-gray-600">
                  {usage.ipAddress && `IP: ${usage.ipAddress}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OTPDetail;
